/**
 * Project detail — tabs estese: Overview, WBS, Team, Milestone, Ore, Gantt, Kanban,
 * Documenti, Storico, Economics.
 */
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Alert, Anchor, Badge, Button, Grid, Group, Menu, Modal,
  SimpleGrid, Stack, Switch, Tabs, Text, rem,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { InlineLoader, PageLoader } from '@/shared/components/LoadingState';
import {
  IconBuildingFactory2, IconCash, IconChartPie, IconClipboardList, IconClock,
  IconProgressCheck,
  IconFileDiff, IconFileText, IconFiles, IconFlag, IconForms, IconGauge, IconHistory, IconReceipt2,
  IconLayoutKanban, IconTag, IconTimeline, IconTransform, IconUsers,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchList, fetchOne, updateOne } from '@/shared/api/jsonapi/client';
import { fetchProjectHoursSummary, fetchProjectEac } from '@/features/projects/api';
import { FavoriteStar } from '@/features/projects/FavoriteStar';
import { HealthBadge, PriorityBadge } from '@/features/projects/HealthBadge';
import { healthLabel } from '@/features/projects/HealthBadge.utils';
import { ProjectEconomicsCard, type EconomicsData } from '@/features/projects/ProjectEconomicsCard';
import { ProjectMilestoneTimeline } from '@/features/projects/ProjectMilestoneTimeline';
import { ProjectWbsTree } from '@/features/projects/ProjectWbsTree';
import { ProjectProgressPanel } from '@/features/projects/ProjectProgressPanel';
import { ProjectGantt } from '@/features/projects/ProjectGantt';
import { ProjectChangeOrders } from '@/features/projects/ProjectChangeOrders';
import { ProjectHourCaps } from '@/features/projects/ProjectHourCaps';
import { ProjectSubActivities } from '@/features/projects/ProjectSubActivities';
import { OrderHoursPanel } from '@/features/orders/OrderHoursPanel';
import { ProjectOrderLinks } from '@/features/orders/ProjectOrderLinks';
import { ProjectTeamManager } from '@/features/projects/ProjectTeamManager';
import { ProjectMaeSpaceRoom } from '@/features/maespace/ProjectMaeSpaceRoom';
import { ProjectMaeSpaceAction } from '@/features/maespace/ProjectMaeSpaceAction';
import { ProjectMaeSpaceFiles } from '@/features/maespace/ProjectMaeSpaceFiles';
import { ProjectCostAllocation } from '@/features/projects/ProjectCostAllocation';
import { ProjectCustomFields } from '@/features/projects/ProjectCustomFields';
import { ProjectReportingRules } from '@/features/projects/ProjectReportingRules';
import { phaseLabel, phaseTransitionOptions, isTerminalPhase } from '@/features/projects/phase';
import { customerLabel } from '@/features/projects/customerLabel';
import { statusColor } from '@/shared/lib/statusColors';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MaePageHead, MaeShell, MaeTabPills } from '@/shared/desktop/MaeShell';
import { ExternalSyncNotice } from '@/features/admin/ExternalSyncNotice';
import { useMaePageTitle } from '@/shared/desktop/MaeShell.utils';
import { mw, mwSectionLabel, MW_CARD_SHADOW, MW_FONT_NUM } from '@/shared/mobile/tokens';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

/** Card MaeDesign riusabile in questa pagina. */
const MW_CARD: React.CSSProperties = { border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW };

/** Sezione MaeDesign con etichetta uppercase + contenuto. */
function SectionCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ ...MW_CARD, padding: 18 }}>
      <Text component="div" style={{ ...mwSectionLabel, marginBottom: 12 }}>{label}</Text>
      {children}
    </div>
  );
}

interface Project extends Denormalized {
  // Anagrafica (BE v1.21: status→phase; rimossi customerName/freeFieldString*/economics legacy)
  name?: string;
  code?: string;
  phase?: string;
  projectType?: string;
  billingModel?: string;
  description?: string;
  notes?: string;
  priority?: string;
  healthStatus?: string;
  riskLevel?: string;
  // Date
  startDate?: string;
  endDate?: string;
  baselineStart?: string;
  baselineEnd?: string;
  actualStart?: string;
  actualEnd?: string;
  deadline?: string;
  /** L'ultima approvazione della WBS (`POST /projects/{id}/wbs/approve`); nullo = mai approvata. */
  wbsApprovedAt?: string | null;
  // Economics (triade v1.21)
  plannedHours?: number;
  /** Tariffa oraria di ripiego: la usa il team quando il membro non ne ha una sua. */
  defaultHourlyRate?: number;
  actualHours?: number;
  budgetRevenue?: number;
  budgetLaborCost?: number;
  budgetExpenseCost?: number;
  invoicedAmount?: number;
  orderNumber?: string;
  contractDate?: string;
  contractValue?: number;
  paymentTerms?: string;
  currency?: string;
  // Regole sulle ore: si scelgono alla creazione e si cambiano dalla panoramica.
  requiresActivityReason?: boolean;
  showActivityReasonPicker?: boolean;
  requiresApproval?: boolean;
  requireReasonForNonBillableHours?: boolean;
  // Group Resource Pool (BE v1.23/v1.25)
  groupStaffingEnabled?: boolean;
  costAllocationMode?: string;
  // Relationships
  customer?: { id: string; type?: string; client?: { organization?: string; name?: string; firstName?: string; lastName?: string } };
  category?: { id: string; type?: string; name?: string; code?: string };
  projectManager?: { id: string; type?: string; firstName?: string; lastName?: string; email?: string };
  projectSponsor?: { id: string; type?: string; firstName?: string; lastName?: string; email?: string };
  /** ORDERS §6.1: destinatario dell'avviso di provisioning voci. */
  businessArea?: { id: string; type?: string; name?: string; code?: string } | null;
  /** ORDERS §2.4: commessa su cui proseguire a monte ore esaurito. */
  nextProject?: { id: string; type?: string; name?: string; code?: string } | null;
}

interface ProjectActivity extends Denormalized {
  type: 'project-activities';
  eventType?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  description?: string;
  createdAt?: string;
}

type TabIcon = typeof IconBuildingFactory2;
const PROJECT_TABS: { value: string; label: string; icon: TabIcon }[] = [
  { value: 'overview', label: 'Overview', icon: IconBuildingFactory2 },
  { value: 'wbs', label: 'WBS', icon: IconClipboardList },
  /* Subito dopo la WBS perché è la sua lettura: l'albero dice come è fatto il lavoro,
     l'avanzamento dice a che punto è. */
  { value: 'progress', label: 'Avanzamento', icon: IconProgressCheck },
  // Le voci su cui si rendiconta: non sono i nodi della WBS.
  { value: 'activities', label: 'Attività', icon: IconTag },
  { value: 'team', label: 'Team', icon: IconUsers },
  { value: 'milestones', label: 'Milestone', icon: IconFlag },
  { value: 'hours', label: 'Ore', icon: IconClock },
  { value: 'gantt', label: 'Gantt', icon: IconTimeline },
  { value: 'kanban', label: 'Kanban', icon: IconLayoutKanban },
  { value: 'change-orders', label: 'Varianti', icon: IconFileDiff },
  { value: 'hour-caps', label: 'Tetti ore', icon: IconGauge },
  // Monte ore VENDUTO (righe d'ordine), distinto dai «Tetti ore» che sono il budget interno.
  { value: 'order-hours', label: 'Monte ore', icon: IconReceipt2 },
  { value: 'docs', label: 'Documenti', icon: IconFiles },
  { value: 'history', label: 'Storico', icon: IconHistory },
  { value: 'custom-fields', label: 'Campi custom', icon: IconForms },
  { value: 'economics', label: 'Economics', icon: IconCash },
];

const TAB_VALUES = new Set(PROJECT_TABS.map((t) => t.value));

/**
 * La tab aperta sta nell'URL (`?tab=wbs`): cosi' un collegamento porta **dentro** la commessa —
 * «Apri il progetto» dall'Andamento WBS atterra sull'albero, non sulla panoramica — e F5
 * ritrova la tab. Un valore sconosciuto non e' un errore: si apre la panoramica. `overview` non
 * si scrive, cosi' l'URL di sempre resta pulito.
 */
export const Route = createFileRoute('/_app/projects/$projectId')({
  validateSearch: (s: Record<string, unknown>): { tab?: string } => ({
    tab: typeof s.tab === 'string' && TAB_VALUES.has(s.tab) && s.tab !== 'overview' ? s.tab : undefined,
  }),
  component: ProjectDetailPage,
});

function ProjectDetailPage() {
  const { projectId } = useParams({ from: '/_app/projects/$projectId' });
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['project', projectId],
    // BE v1.20.12/v1.21: label cliente dal client del tenant-client; PM/sponsor sono erp-employees
    // (archi ciclici → include esplicito). SplitQuery lato BE (v1.22) regge l'include multiplo.
    queryFn: () => fetchOne<Project>('projects', projectId, { include: ['customer.client', 'category', 'projectManager', 'projectSponsor', 'businessArea', 'nextProject'] }),
  });
  const isMobile = useIsMobileShell();
  const { tab: tabFromUrl } = Route.useSearch();
  const tab = tabFromUrl ?? 'overview';
  /* `replace`: cambiare tab non e' una pagina nuova, e Indietro deve uscire dalla commessa. */
  const setTab = (v: string | null) => void navigate({
    to: '/projects/$projectId',
    params: { projectId },
    search: (prev) => ({ ...prev, tab: v && v !== 'overview' ? v : undefined }),
    replace: true,
  });
  /* Il nome della commessa fa da titolo nell'app bar della shell mobile: senza, la barra
     mostrerebbe il nome di chi guarda. Prima dei rientri anticipati: un hook si chiama sempre. */
  useMaePageTitle(data?.name ?? 'Progetto');

  // Spinner centrato nella pagina: `<Loader />` nudo restava incollato in alto a sinistra.
  if (isError) return <QueryErrorAlert error={error} onRetry={() => void refetch()} />;
  if (isLoading) return <PageLoader label="Carico il progetto…" />;
  if (!data) return <Text c="dimmed">Progetto non trovata</Text>;

  const customerName = customerLabel(data.customer);
  const categoryName = data.category && 'name' in data.category ? data.category.name : undefined;

  const inner = (
    <>
      {/* Testata prima di tutto: chiusa, sale sotto l'header. */}
      <MaePageHead
        onBack={() => void navigate({ to: '/projects' })}
        backLabel="Torna ai progetti"
        title={data.name ?? 'Progetto'}
        sub={customerName ? `Cliente: ${customerName}${categoryName ? ` · ${categoryName}` : ''}` : undefined}
        icon={<IconBuildingFactory2 size={23} />}
        badges={<>
          {data.code && <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: mw.accentTint, color: mw.accentText, fontFamily: MW_FONT_NUM }}>{data.code}</span>}
          <PhaseControl projectId={projectId} phase={data.phase} />
          {data.priority && <PriorityBadge value={data.priority} />}
          <HealthBadge value={data.healthStatus} />
          <FavoriteStar projectId={projectId} size={20} />
        </>}
        actions={
          <Group gap={8} wrap="nowrap">
            <ProjectMaeSpaceAction projectId={projectId} />
            <button
              onClick={() => void navigate({ to: `/projects/${projectId}/dashboard` })}
              className="mw-pressable"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: rem(40), padding: '0 16px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${mw.line}`, background: mw.surface, color: mw.ink900, fontSize: 'var(--fs-body)', fontWeight: 600, fontFamily: 'inherit', flexShrink: 0 }}
            >
              <IconChartPie size={16} /> Dashboard
            </button>
          </Group>
        }
      />
      <ExternalSyncNotice resourceType="projects" records={[data]} />
      {/* La descrizione può essere lunga: sta sotto, non nel sottotitolo. */}
      {data.description && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginBottom: 20, lineHeight: 1.5 }}>{data.description}</Text>}

      <Tabs value={tab} onChange={(v) => setTab(v ?? 'overview')} keepMounted={false}>
        <MaeTabPills tabs={PROJECT_TABS} active={tab} onChange={setTab} />

        <Tabs.Panel value="overview">
          <OverviewPanel project={data} />
        </Tabs.Panel>
        <Tabs.Panel value="wbs" pt="md"><ProjectWbsTree projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="progress" pt="md"><ProjectProgressPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="activities" pt="md"><ProjectSubActivities projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="team" pt="md">
          <Stack gap="md">
            <ProjectMaeSpaceRoom projectId={projectId} />
            <ProjectTeamManager projectId={projectId} currency={data.currency ?? 'EUR'} defaultHourlyRate={data.defaultHourlyRate} />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="milestones" pt="md"><ProjectMilestoneTimeline projectId={projectId} currency={data.currency ?? 'EUR'} /></Tabs.Panel>
        <Tabs.Panel value="hours" pt="md"><HoursPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="gantt" pt="md"><ProjectGantt projectId={projectId} projectName={data.name ?? undefined} /></Tabs.Panel>
        <Tabs.Panel value="kanban" pt="md">
          <KanbanRedirectPanel projectId={projectId} />
        </Tabs.Panel>
        <Tabs.Panel value="change-orders" pt="md"><ProjectChangeOrders projectId={projectId} currency={data.currency ?? 'EUR'} /></Tabs.Panel>
        <Tabs.Panel value="hour-caps" pt="md"><ProjectHourCaps projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="order-hours" pt="md">
          <Stack gap={14}>
            <ProjectOrderLinks
              projectId={projectId}
              businessArea={data.businessArea}
              nextProject={data.nextProject}
            />
            <OrderHoursPanel projectId={projectId} />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="docs" pt="md">
          <Stack gap={14}>
            {/* I documenti della stanza MaeSpace (F3): non disegna niente a modulo spento. */}
            <ProjectMaeSpaceFiles projectId={projectId} />
            <DocsPanel />
          </Stack>
        </Tabs.Panel>
        <Tabs.Panel value="history" pt="md"><HistoryPanel projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="custom-fields" pt="md"><ProjectCustomFields projectId={projectId} /></Tabs.Panel>
        <Tabs.Panel value="economics" pt="md">
          <Stack>
            <EconomicsPanel project={data} projectId={projectId} />
            <ProjectCostAllocation projectId={projectId} mode={data.costAllocationMode} currency={data.currency ?? 'EUR'} />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </>
  );

  if (isMobile) {
    return (
      <div style={{ padding: '16px 16px 28px' }}>
        {inner}
      </div>
    );
  }

  return <MaeShell>{inner}</MaeShell>;
}

function OverviewPanel({ project }: { project: Project }) {
  const pmName = project.projectManager ? `${project.projectManager.firstName ?? ''} ${project.projectManager.lastName ?? ''}`.trim() || project.projectManager.email || project.projectManager.id : undefined;
  const sponsorName = project.projectSponsor ? `${project.projectSponsor.firstName ?? ''} ${project.projectSponsor.lastName ?? ''}`.trim() || project.projectSponsor.email || project.projectSponsor.id : undefined;
  const deadlineLate = project.deadline ? dayjs(project.deadline).isBefore(dayjs()) : false;
  return (
    <Grid gap="lg" align="start">
      <Grid.Col span={{ base: 12, md: 8 }}>
        <Stack gap="md">
          <SectionCard label="Anagrafica">
            <Stack gap={10}>
              <FieldRow label="Codice" value={project.code} />
              <FieldRow label="Nome" value={project.name} />
              <FieldRow label="Cliente" value={customerLabel(project.customer)} />
              <FieldRow label="Categoria" value={project.category && 'name' in project.category ? project.category.name : undefined} />
              <FieldRow label="Project Manager" value={pmName} />
              <FieldRow label="Sponsor esecutivo" value={sponsorName} />
              <FieldRow label="Descrizione" value={project.description} />
            </Stack>
          </SectionCard>

          <SectionCard label="Date & baseline">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={10} verticalSpacing={10}>
              <FieldRow label="Inizio" value={fmtDate(project.startDate)} />
              <FieldRow label="Fine" value={fmtDate(project.endDate)} />
              <FieldRow label="Baseline inizio" value={fmtDate(project.baselineStart)} />
              <FieldRow label="Baseline fine" value={fmtDate(project.baselineEnd)} />
              <FieldRow label="Effettivo inizio" value={fmtDate(project.actualStart)} />
              <FieldRow label="Effettivo fine" value={fmtDate(project.actualEnd)} />
              <FieldRow label="Deadline" value={fmtDate(project.deadline)} />
              <FieldRow label="WBS approvata il" value={project.wbsApprovedAt ? fmtDate(project.wbsApprovedAt) : 'Non ancora approvata'} />
            </SimpleGrid>
          </SectionCard>

          <SectionCard label="Health & priorità">
            <Group gap={24} wrap="wrap">
              <Group gap={8}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Priorità</Text><PriorityBadge value={project.priority} /></Group>
              <Group gap={8}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Health</Text><HealthBadge value={project.healthStatus} /></Group>
              <Group gap={8}><Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Rischio</Text><span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: mw.surface2, color: mw.ink500 }}>{project.riskLevel ?? '—'}</span></Group>
            </Group>
          </SectionCard>

          <SectionCard label="Note">
            <Text style={{ fontSize: 'var(--fs-compact)', color: project.notes ? mw.ink900 : mw.ink300, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{project.notes || '—'}</Text>
          </SectionCard>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 4 }}>
        <Stack gap="md">
          <div style={{ ...MW_CARD, padding: 18 }}>
            <Text component="div" style={{ ...mwSectionLabel, marginBottom: 12 }}>Stato salute</Text>
            <Stack gap={10}>
              <HealthBadge value={project.healthStatus} size="lg" />
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{healthLabel(project.healthStatus)}</Text>
              {project.deadline && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderRadius: 10, padding: '10px 12px', background: deadlineLate ? mw.dangerBg : mw.accentTint, color: deadlineLate ? mw.danger : mw.accentText }}>
                  <IconClock size={15} style={{ flexShrink: 0 }} />
                  <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600 }}>Deadline: {fmtDate(project.deadline)}</Text>
                </div>
              )}
            </Stack>
          </div>
          <GroupStaffingToggle projectId={project.id} enabled={project.groupStaffingEnabled !== false} />
          <ProjectReportingRules projectId={project.id} project={project} />
        </Stack>
      </Grid.Col>
    </Grid>
  );
}

/** Interruttore per-progetto dello staffing di gruppo (BE v1.23). Può solo RESTRINGERE:
 *  se il pool è spento sulla radice (`tenant.enableGroupPool`) resta comunque disattivo. */
function GroupStaffingToggle({ projectId, enabled }: { projectId: string; enabled: boolean }) {
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (next: boolean) => updateOne('projects', { id: projectId, attributes: { groupStaffingEnabled: next } }),
    onSuccess: () => { notifySuccess('Impostazione aggiornata'); void qc.invalidateQueries({ queryKey: ['project', projectId] }); },
    onError: (err) => notifyError(err),
  });
  return (
    <div style={{ ...MW_CARD, padding: 18 }}>
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div style={{ minWidth: 0 }}>
          <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900 }}>Staffing di gruppo</Text>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 2, lineHeight: 1.5 }}>Consente di assegnare dipendenti di altre società del gruppo a questo progetto.</Text>
        </div>
        <Switch
          checked={enabled}
          onChange={(e) => toggle.mutate(e.currentTarget.checked)}
          disabled={toggle.isPending}
          aria-label="Attiva staffing di gruppo"
        />
      </Group>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 600, color: mw.ink300, minWidth: rem(140), flexShrink: 0 }}>{label}</Text>
      <Text style={{ fontSize: 'var(--fs-compact)', color: value != null && value !== '' ? mw.ink900 : mw.ink300 }}>{value != null && value !== '' ? value : '—'}</Text>
    </div>
  );
}

function fmtDate(d?: string | null): string | null {
  if (!d) return null;
  return dayjs(d).format('DD/MM/YYYY');
}

function HoursPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-hours-panel', projectId],
    queryFn: () => fetchProjectHoursSummary(projectId),
  });
  if (isLoading) return <InlineLoader label="Carico il consuntivo ore…" />;
  if (!data) return <Text c="dimmed">Nessun dato.</Text>;
  const row = (label: string, value: string) => (
    <Group justify="space-between">
      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>{label}</Text>
      <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 700, color: mw.ink900, fontFamily: MW_FONT_NUM }}>{value}</Text>
    </Group>
  );
  return (
    <SectionCard label="Consuntivo ore">
      <Stack gap={12}>
        {row('Ore ordinarie', `${data.totalOrdinaryHours.toFixed(1)} h`)}
        {row('Ore fatturabili', `${data.totalBillingHours.toFixed(1)} h`)}
        {row('Spese', `€ ${data.totalExpenses.toFixed(2)}`)}
        {row('Voci', String(data.entriesCount))}
        {data.budgetRevenue != null && row('Budget ricavi', `€ ${data.budgetRevenue.toFixed(2)}`)}
      </Stack>
    </SectionCard>
  );
}

function KanbanRedirectPanel({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  return (
    <div style={{ ...MW_CARD, padding: 32, textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px', background: mw.accentTint, color: mw.accentText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconLayoutKanban size={28} />
      </div>
      <Text style={{ fontSize: 'var(--fs-lead)', fontWeight: 700, color: mw.ink900 }}>Kanban del progetto</Text>
      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 4, maxWidth: rem(360), marginInline: 'auto', lineHeight: 1.5 }}>Apri la board kanban filtrata per i task di questo progetto.</Text>
      <button
        onClick={() => void navigate({ to: '/tasks/kanban', search: { projectId } as never })}
        className="mw-pressable"
        style={{ marginTop: 16, height: rem(40), padding: '0 18px', borderRadius: 10, cursor: 'pointer', border: 0, background: mw.brandFill, color: 'var(--on-brand)', fontSize: 'var(--fs-body)', fontWeight: 700, fontFamily: 'inherit' }}
      >
        Vai a Kanban
      </button>
    </div>
  );
}

/**
 * Tab Documenti — **responsive**: su schermo largo la spiegazione e le azioni stanno
 * affiancate, sotto i 700px vanno in colonna con i bottoni a larghezza piena (prima
 * l'azione era in un `Group justify="flex-end"` che su mobile finiva schiacciato).
 *
 * Il caricamento diretto sulla commessa non c'è sul BE: il bottone «Carica file» spento con la
 * spiegazione è stato tolto il 07-10-2026: era un comando morto, e dove MaeSpace è acceso un posto
 * dove caricare c'è — i documenti della stanza, sopra questo riquadro, per chi è nella stanza.
 * Il testo non lo nomina perché a modulo spento quel riquadro non c'è. Resta il rimando alla
 * Media Library, che è la strada per i file di MaeWay.
 */
function DocsPanel() {
  return (
    <SectionCard label="Documenti">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'stretch' }}>
        <div style={{
          flex: '2 1 320px', minWidth: 0, display: 'flex', gap: 10, alignItems: 'flex-start',
          borderRadius: 12, padding: '12px 14px', background: mw.accentTint,
        }}>
          <IconFileText size={17} style={{ color: mw.accentText, flexShrink: 0, marginTop: 1 }} />
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, lineHeight: 1.5 }}>
            I file di MaeWay si gestiscono dalla Media Library globale e si collegano al
            progetto dal tag.
          </Text>
        </div>
        <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
          <Anchor component={Link} to="/media" size="sm" c={mw.accentText} style={{ textAlign: 'center' }}>
            Apri Media Library →
          </Anchor>
        </div>
      </div>
    </SectionCard>
  );
}

function HistoryPanel({ projectId }: { projectId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-activities', projectId],
    queryFn: () => fetchList<ProjectActivity>('project-activities', {
      filter: { 'project.id': { eq: projectId } },
      sort: ['-createdAt'],
      page: { size: 100 },
    }),
  });
  if (isLoading) return <InlineLoader label="Carico lo storico…" />;
  const list = data?.data ?? [];
  if (list.length === 0) {
    return (
      <div style={{ ...MW_CARD, padding: '24px 16px', textAlign: 'center' }}>
        <span style={{ display: 'inline-flex', width: 44, height: 44, borderRadius: 12, background: mw.surface2, color: mw.ink300, alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}><IconHistory size={22} /></span>
        <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>Nessuna attività registrata.</Text>
      </div>
    );
  }
  return (
    <Stack gap={0} style={{ ...MW_CARD, padding: '4px 16px' }}>
      {list.map((a, i) => (
        <Group key={a.id} justify="space-between" gap="xs" wrap="nowrap" align="flex-start" style={{ padding: '12px 0', borderTop: i > 0 ? `1px solid ${mw.line}` : undefined }}>
          <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6}>
              <span style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: mw.accentTint, color: mw.accentText }}>{a.eventType ?? 'event'}</span>
              {a.field && <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 700, color: mw.ink900 }}>{a.field}</Text>}
            </Group>
            {a.description && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink900 }}>{a.description}</Text>}
            {(a.oldValue || a.newValue) && (
              <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>
                {a.oldValue && <>da <Text span c={mw.danger}>{a.oldValue}</Text></>}
                {a.oldValue && a.newValue && ' '}
                {a.newValue && <>a <Text span c={mw.success}>{a.newValue}</Text></>}
              </Text>
            )}
          </Stack>
          <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300, flexShrink: 0, fontFamily: MW_FONT_NUM }}>{a.createdAt ? dayjs(a.createdAt).format('DD/MM/YY HH:mm') : ''}</Text>
        </Group>
      ))}
    </Stack>
  );
}

function EconomicsPanel({ project, projectId }: { project: Project; projectId: string }) {
  const hoursQ = useQuery({
    queryKey: ['project-hours-summary', projectId],
    queryFn: () => fetchProjectHoursSummary(projectId),
  });
  // BE v1.21: EAC/ETC/variance non sono attributi → endpoint dedicato.
  const eacQ = useQuery({
    queryKey: ['project-eac', projectId],
    queryFn: () => fetchProjectEac(projectId),
  });
  const econ: EconomicsData = {
    plannedHours: project.plannedHours,
    actualHours: project.actualHours,
    budgetRevenue: project.budgetRevenue,
    budgetLaborCost: project.budgetLaborCost,
    budgetExpenseCost: project.budgetExpenseCost,
    invoicedAmount: project.invoicedAmount,
    currency: project.currency,
    orderNumber: project.orderNumber,
    contractDate: project.contractDate,
    contractValue: project.contractValue,
    paymentTerms: project.paymentTerms,
  };
  return <ProjectEconomicsCard data={econ} hoursUsed={hoursQ.data?.totalOrdinaryHours} eac={eacQ.data} />;
}

/**
 * Badge fase + cambio stato guidato dalla state-machine BE (v1.22): il menu propone
 * solo le transizioni valide dallo stato corrente (matrice in phase.ts); il 422
 * INVALID_STATUS_TRANSITION resta comunque gestito come rete di sicurezza.
 */
function PhaseControl({ projectId, phase }: { projectId: string; phase?: string }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<string | null>(null);
  const options = phaseTransitionOptions(phase).filter((o) => o.value !== (phase ?? 'draft'));
  const terminal = isTerminalPhase(phase);

  const change = useMutation({
    mutationFn: (next: string) => updateOne('projects', { id: projectId, attributes: { phase: next } }),
    onSuccess: () => {
      notifySuccess('Fase aggiornata');
      void qc.invalidateQueries({ queryKey: ['project', projectId] });
      setTarget(null);
    },
    onError: (err) => notifyError(err),
  });

  return (
    <>
      <Menu shadow="md" position="bottom-start" disabled={terminal || options.length === 0}>
        <Menu.Target>
          {/* Vedi `LogsConsole`: un bersaglio di menu deve essere un pulsante, o col tastierino
              non si apre. */}
          <Badge
            component="button"
            type="button"
            color={statusColor(phase)}
            rightSection={!terminal && options.length > 0 ? <IconTransform size={12} /> : undefined}
            style={{ cursor: terminal || options.length === 0 ? 'default' : 'pointer' }}
          >
            {phaseLabel(phase)}
          </Badge>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Cambia fase</Menu.Label>
          {options.map((o) => (
            <Menu.Item key={o.value} onClick={() => setTarget(o.value)}>{o.label}</Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      <Modal opened={target !== null} onClose={() => setTarget(null)} title="Conferma cambio fase" centered>
        <Stack>
          <Text size="sm">
            Confermi il passaggio da <strong>{phaseLabel(phase)}</strong> a <strong>{phaseLabel(target)}</strong>?
          </Text>
          {(target === 'cancelled' || target === 'archived') && (
            <Alert color="orange" variant="light">Questa transizione è difficilmente reversibile.</Alert>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setTarget(null)}>Annulla</Button>
            <Button loading={change.isPending} onClick={() => target && change.mutate(target)}>Conferma</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
